/** Human-readable labels for backoffice audit action codes. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  ORG_VIEW: 'Viewed organization',
  ORG_CREATE: 'Created organization',
  ORG_SUSPEND: 'Suspended organization',
  ORG_UNSUSPEND: 'Restored organization',
  ORG_DELETE: 'Archived organization',
  ORG_DATA_EXPORT: 'Exported organization data',
  IMPERSONATE_START: 'Started impersonation',
  IMPERSONATE_END: 'Ended impersonation',
  ANNOUNCEMENT_CREATE: 'Published announcement',
  ANNOUNCEMENT_UPDATE: 'Updated announcement',
  ANNOUNCEMENT_DEACTIVATE: 'Took down announcement',
  ADMIN_INVITE: 'Added administrator',
  ADMIN_DISABLE: 'Disabled administrator',
  ADMIN_ENABLE: 'Enabled administrator',
  USER_SESSIONS_TERMINATED: 'Signed user out everywhere',
  USER_VERIFICATION_RESENT: 'Resent verification email',
  USER_PASSWORD_RESET_REQUESTED: 'Sent password reset',
  BILLING_PLAN_ASSIGNED: 'Changed plan',
  BILLING_SUBSCRIPTION_CANCELLED: 'Cancelled subscription',
  BILLING_REFUND: 'Issued refund',
  BILLING_CREDIT: 'Issued credit',
  BILLING_CHECKOUT_LINK: 'Sent payment link',
  ORG_NOTE_CREATE: 'Added staff note',
  CASE_CREATE: 'Opened a case',
  CASE_UPDATE: 'Updated a case',
  INTEGRATION_DISCONNECT: 'Disconnected integration',
  INTEGRATION_REAUTH: 'Asked for reconnection',
  EVENT_RETRY: 'Retried a failed event',
  FLAG_UPDATE: 'Changed a kill-switch',
  DENIED: 'Access denied',
};

const PROVIDER_LABELS: Record<string, string> = {
  google_sheets: 'Google Sheets',
  google: 'Google',
  slack: 'Slack',
  zoho: 'Zoho',
  flutterwave: 'Flutterwave',
  paystack: 'Paystack',
  resend: 'Email (Resend)',
};

const TARGET_LABELS: Record<string, string> = {
  BackofficeCase: 'Case',
  BackofficeOrgNote: 'Note',
  FeatureFlag: 'Kill-switch',
  Organization: 'Organization',
  Administrator: 'Administrator',
  User: 'User',
  Announcement: 'Announcement',
  IntegrationConnection: 'Integration',
  EventOutbox: 'Event',
  BillingSubscription: 'Subscription',
};

const ACTOR_LABELS: Record<string, string> = {
  ADMINISTRATOR: 'Staff',
  USER: 'Customer',
  SYSTEM: 'System',
  UNAUTHENTICATED: 'Unsigned-in visitor',
};

const SUBSYSTEM_LABELS: Record<string, string> = {
  api: 'API',
  database: 'Database',
  events: 'Events',
  notifications: 'Notifications',
  email: 'Email',
  files: 'Files',
  integrations: 'Integrations',
  imports: 'Imports',
  inventory: 'Inventory',
  sales: 'Sales',
  accounting: 'Accounting',
  procurement: 'Buying',
};

const JOB_LABELS: Record<string, string> = {
  'events.dispatcher': 'Event delivery',
  'notifications.escalation': 'Notification follow-up',
  'notifications.delivery.retry': 'Notification retry',
  'notifications.delivery_retry': 'Notification retry',
  'notifications.retention': 'Notification cleanup',
  'accounting.recurring': 'Recurring accounting',
};

const FLAG_LABELS: Record<string, string> = {
  'platform.api.kill_switch': 'Stop all customer apps',
  'org.api.kill_switch': 'Stop one organization',
};

const SETTING_LABELS: Record<string, string> = {
  'platform.displayName': 'Product name',
  'platform.adminIpAllowlist': 'Staff IP allowlist',
};

const FLAG_HELP: Record<string, string> = {
  'platform.api.kill_switch': 'Stops every customer app until you turn this off.',
  'org.api.kill_switch': 'Stops one organization’s apps when you turn it on for them.',
};

const IMPORT_TYPE_LABELS: Record<string, string> = {
  WORKBOOK: 'Spreadsheet',
  ITEMS: 'Items',
  CUSTOMERS: 'Customers',
  SUPPLIERS: 'Suppliers',
  STOCK: 'Stock',
  HISTORICAL_GL: 'Accounting history',
};

const ROLLOUT_LABELS: Record<string, string> = {
  OFF: 'Off',
  ON: 'On',
  PER_ORG: 'Per organization',
};

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const CUID_RE = /^c[a-z0-9]{20,}$/i;
const HEX_SECRET_RE = /^[a-f0-9]{32,}$/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const HIDDEN_SNAPSHOT_KEYS = new Set([
  'id',
  'ids',
  'impersonationkey',
  'impersonation_key',
  'targetuserid',
  'targetid',
  'organizationid',
  'administratorid',
  'userid',
  'actorid',
  'sessionid',
  'correlationid',
  'requestid',
  'resourceid',
  'workitemid',
  'caseid',
  'planid',
  'invoiceid',
  'secret',
  'token',
  'ciphertext',
  'password',
  'key',
]);

const SNAPSHOT_FIELD_LABELS: Record<string, string> = {
  reason: 'Reason',
  ticketId: 'Ticket',
  ticketRef: 'Ticket',
  expiresAt: 'Expires',
  createdAt: 'When',
  updatedAt: 'Updated',
  status: 'Status',
  title: 'Title',
  body: 'Detail',
  amount: 'Amount',
  currency: 'Currency',
  currencyCode: 'Currency',
  plan: 'Plan',
  planKey: 'Plan',
  planName: 'Plan',
  rollout: 'State',
  enabled: 'On for this organization',
  memo: 'Note',
  filename: 'File',
  provider: 'Provider',
  providerKey: 'Provider',
  interval: 'Billing interval',
};

export function looksLikeId(value: string): boolean {
  const trimmed = value.trim();
  return UUID_RE.test(trimmed) || CUID_RE.test(trimmed) || HEX_SECRET_RE.test(trimmed);
}

export type SnapshotRow = { label: string; value: string };

function isHiddenSnapshotKey(key: string): boolean {
  const normalized = key.replace(/[_-]/g, '').toLowerCase();
  if (HIDDEN_SNAPSHOT_KEYS.has(normalized)) return true;
  if (normalized === 'ticketid' || normalized === 'ticketref') return false;
  if (normalized.endsWith('id') || normalized.endsWith('ids')) return true;
  if (normalized.endsWith('key') && normalized !== 'plankey' && normalized !== 'providerkey') return true;
  return false;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatOperatorScalar(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
    if (looksLikeId(trimmed)) return null;
    if (ISO_DATE_RE.test(trimmed)) return formatDateTime(trimmed);
    try {
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        const parsed: unknown = JSON.parse(trimmed);
        const rows = formatSnapshot(parsed);
        if (rows.length === 0) return null;
        return rows.map((row) => `${row.label}: ${row.value}`).join(' · ');
      }
    } catch {
      /* not JSON */
    }
    return displayEntityName(trimmed, trimmed);
  }
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    const rows = formatSnapshot(value);
    if (rows.length === 0) return null;
    return rows.map((row) => `${row.label}: ${row.value}`).join(' · ');
  }
  return null;
}

function snapshotFieldLabel(key: string): string {
  return SNAPSHOT_FIELD_LABELS[key] ?? humanizeCode(key);
}

/** Flatten before/after audit payloads into operator-readable rows. Never includes IDs, keys, or JSON. */
export function formatSnapshot(value: unknown): SnapshotRow[] {
  if (value == null) return [];
  if (typeof value !== 'object') {
    const formatted = formatOperatorScalar(value);
    return formatted ? [{ label: 'Detail', value: formatted }] : [];
  }
  const rows: SnapshotRow[] = [];
  const items = Array.isArray(value)
    ? value.map((item, index) => [String(index), item] as const)
    : Object.entries(value as Record<string, unknown>);
  for (const [key, entry] of items) {
    if (!Array.isArray(value) && isHiddenSnapshotKey(key)) continue;
    if (entry != null && typeof entry === 'object' && !Array.isArray(entry)) {
      rows.push(...formatSnapshot(entry));
      continue;
    }
    const formatted = formatOperatorScalar(entry);
    if (!formatted) continue;
    rows.push({ label: Array.isArray(value) ? snapshotFieldLabel(String(key)) : snapshotFieldLabel(key), value: formatted });
  }
  return rows;
}

export function displayEntityName(name: string | null | undefined, fallback = 'Unknown'): string {
  if (!name?.trim()) return fallback;
  const trimmed = name.trim();
  if (looksLikeId(trimmed)) return fallback;
  return trimmed;
}

export function humanizeCode(value: string | null | undefined): string {
  if (!value?.trim()) return '—';
  const trimmed = value.trim();
  const mapped =
    AUDIT_ACTION_LABELS[trimmed] ??
    PROVIDER_LABELS[trimmed] ??
    TARGET_LABELS[trimmed] ??
    ACTOR_LABELS[trimmed] ??
    SUBSYSTEM_LABELS[trimmed] ??
    JOB_LABELS[trimmed] ??
    FLAG_LABELS[trimmed] ??
    SETTING_LABELS[trimmed] ??
    IMPORT_TYPE_LABELS[trimmed] ??
    ROLLOUT_LABELS[trimmed];
  if (mapped) return mapped;
  if (looksLikeId(trimmed)) return '—';
  return trimmed
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[._:/]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatAuditAction(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? humanizeCode(action);
}

export function formatProvider(key: string): string {
  return PROVIDER_LABELS[key] ?? humanizeCode(key);
}

export function formatTargetKind(kind: string | null | undefined): string {
  if (!kind) return '—';
  return TARGET_LABELS[kind] ?? humanizeCode(kind);
}

export function formatActorType(type: string | null | undefined): string {
  if (!type) return 'Someone';
  if (ACTOR_LABELS[type]) return ACTOR_LABELS[type];
  if (looksLikeId(type)) return 'Someone';
  if (type.includes('_') || type === type.toUpperCase()) return humanizeCode(type);
  return type;
}

export function formatSubsystem(key: string): string {
  return SUBSYSTEM_LABELS[key] ?? humanizeCode(key);
}

export function formatJob(name: string): string {
  return JOB_LABELS[name] ?? humanizeCode(name);
}

export function formatFlag(key: string): string {
  return FLAG_LABELS[key] ?? humanizeCode(key);
}

export function formatFlagHelp(key: string, fallback?: string | null): string {
  return FLAG_HELP[key] ?? fallback ?? '';
}

export function formatRollout(value: string): string {
  return ROLLOUT_LABELS[value] ?? humanizeCode(value);
}

export function formatRole(role: string): string {
  return humanizeCode(role);
}
